import React, { useState } from 'react';
import { Upload, Crop, Trash2 } from 'lucide-react';
import type { SplitHeroData, Page, Component } from '../ComponentEditModal.types';
import { ButtonsEditor } from '../shared/ButtonsEditor';
import type { ButtonData } from '../shared/ButtonsEditor';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';
import { ImageCropModal } from '../shared/ImageCropModal';
import type { CropData } from '../shared/ImageCropModal';
import type { Area } from 'react-easy-crop';
import { getCroppedImageStyles, hasCropApplied } from '../shared/cropUtils';
import { NumberInput } from '../shared/NumberInput';
import { RichTextEditor } from '../../RichTextEditor';

interface SplitHeroEditorProps {
  formData: SplitHeroData;
  setFormData: (data: SplitHeroData) => void;
  pages: Page[];
  isLoadingPages: boolean;
  onOpenMediaUpload: (mediaType: 'image', assetType: 'image') => void;
  currentPageComponents?: Component[];
}

export const SplitHeroEditor: React.FC<SplitHeroEditorProps> = ({
  formData,
  setFormData,
  pages,
  isLoadingPages,
  onOpenMediaUpload,
  currentPageComponents,
}) => {
  const data = formData;
  const themeColors = useCustomColors();
  const colorFields = getColorFieldsForComponent('split-hero', themeColors);

  const handleButtonsChange = (buttons: ButtonData[]) => {
    setFormData({ ...data, buttons: buttons as SplitHeroData['buttons'] });
  };

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: {
        ...data.customColors,
        [key]: value,
      },
    });
  };

  return (
    <div className="space-y-6">
      {/* Text Content Section */}
      <TextContentSection data={data} setFormData={setFormData} />

      {/* Layout Options */}
      <LayoutSection data={data} setFormData={setFormData} />

      {/* Width Controls */}
      <WidthControl data={data} setFormData={setFormData} />

      {/* Buttons Section */}
      <ButtonsEditor
        buttons={(data.buttons || []) as ButtonData[]}
        onChange={handleButtonsChange}
        pages={pages}
        isLoadingPages={isLoadingPages}
        currentPageComponents={currentPageComponents}
      />

      {/* Image Section */}
      <ImageSection data={data} setFormData={setFormData} onOpenMediaUpload={onOpenMediaUpload} />

      {/* Responsive Options */}
      <ResponsiveSection data={data} setFormData={setFormData} />

      {/* Image presentation + colors (cutout background lives with colors) */}
      <ImagePresentationSection data={data} setFormData={setFormData} />

      <ColorsSection
        data={data}
        setFormData={setFormData}
        colorFields={
          data.imagePresentation === 'cutout'
            ? colorFields.map(field =>
                field.key === 'background'
                  ? {
                      ...field,
                      label: 'Hero Background',
                      allowCssColor: true,
                      hint: 'Solid hex or use the gradient controls below',
                    }
                  : field
              )
            : colorFields
        }
        onUpdateColor={updateCustomColor}
      />

      {data.imagePresentation === 'cutout' && (
        <CutoutBackgroundSection data={data} setFormData={setFormData} />
      )}
    </div>
  );
};

// Sub-components

interface SectionProps {
  data: SplitHeroData;
  setFormData: (data: SplitHeroData) => void;
}

const TextContentSection: React.FC<SectionProps> = ({ data, setFormData }) => (
  <>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Tagline (optional)</label>
      <textarea
        value={data.tagline || ''}
        onChange={e => setFormData({ ...data, tagline: e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        rows={2}
        placeholder="Enter tagline (press Enter for line break)"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
      <textarea
        value={data.title || ''}
        onChange={e => setFormData({ ...data, title: e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        rows={2}
        placeholder="Enter main title (press Enter for line break)"
        required
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Subtitle / Description (optional)
      </label>
      <RichTextEditor
        content={data.subtitle || ''}
        onChange={html => setFormData({ ...data, subtitle: html })}
        placeholder="Enter subtitle or description"
        minHeight="120px"
      />
      <p className="text-xs text-gray-500 mt-1">Use the toolbar to format your text</p>
    </div>
  </>
);

const LayoutSection: React.FC<SectionProps> = ({ data, setFormData }) => (
  <>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Layout</label>
        <select
          value={data.layout || 'image-right'}
          onChange={e => setFormData({ ...data, layout: e.target.value as any })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="image-left">Image Left</option>
          <option value="image-right">Image Right</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Alignment</label>
        <select
          value={data.alignment || 'left'}
          onChange={e => setFormData({ ...data, alignment: e.target.value as any })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
        </select>
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Size</label>
      <select
        value={data.size || 'large'}
        onChange={e => setFormData({ ...data, size: e.target.value as any })}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
      >
        <option value="small">Small</option>
        <option value="medium">Medium</option>
        <option value="large">Large</option>
      </select>
    </div>
  </>
);

const ImagePresentationSection: React.FC<SectionProps> = ({ data, setFormData }) => {
  const handlePresentationChange = (value: 'fill' | 'cutout') => {
    const next: SplitHeroData = {
      ...data,
      imagePresentation: value,
    };
    if (value === 'cutout') {
      next.image = {
        ...(data.image || { enabled: true }),
        enabled: data.image?.enabled !== false,
        size: 'contain',
        position: data.image?.position || 'bottom',
      };
    }
    setFormData(next);
  };

  return (
    <div className="border-t pt-6">
      <h3 className="text-md font-medium text-gray-900 mb-4">Image presentation</h3>
      <select
        value={data.imagePresentation || 'fill'}
        onChange={e => handlePresentationChange(e.target.value as 'fill' | 'cutout')}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
      >
        <option value="fill">Fill (photo panel)</option>
        <option value="cutout">Cutout (foreground on shared background)</option>
      </select>
      {data.imagePresentation === 'cutout' && (
        <p className="text-xs text-gray-500 mt-1">
          Best with a transparent PNG/WebP subject. The hero background color or gradient shows
          behind both text and image.
        </p>
      )}
    </div>
  );
};

const parseTwoStopGradient = (value?: string | null): { from: string; to: string } | null => {
  if (!value?.startsWith('linear-gradient')) return null;
  const colors = value.match(/#[0-9A-Fa-f]{6}/g);
  if (!colors || colors.length < 2) return null;
  return { from: colors[0], to: colors[1] };
};

const CutoutBackgroundSection: React.FC<SectionProps> = ({ data, setFormData }) => {
  const bg = data.customColors?.background || data.backgroundColor || '';
  const parsed = parseTwoStopGradient(bg);
  const isGradient = !!parsed || (!!bg && bg.startsWith('linear-gradient'));
  const fromColor = parsed?.from || '#f97316';
  const toColor = parsed?.to || '#7c3aed';

  const setBackground = (value: string | null) => {
    setFormData({
      ...data,
      customColors: {
        ...data.customColors,
        background: value,
      },
    });
  };

  return (
    <div className="border-t pt-6 space-y-4">
      <h3 className="text-md font-medium text-gray-900">Hero background style</h3>
      <label className="flex items-center space-x-2 cursor-pointer">
        <input
          type="checkbox"
          checked={isGradient}
          onChange={e => {
            if (e.target.checked) {
              setBackground(`linear-gradient(180deg, ${fromColor}, ${toColor})`);
            } else {
              setBackground(fromColor);
            }
          }}
          className="w-4 h-4 text-primary-600 border-gray-300 rounded-md focus:ring-primary-500"
        />
        <span className="text-sm text-gray-700">Use two-color gradient</span>
      </label>

      {isGradient && (
        <div className="grid grid-cols-2 gap-4 pl-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
            <input
              type="color"
              value={fromColor}
              onChange={e =>
                setBackground(`linear-gradient(180deg, ${e.target.value}, ${toColor})`)
              }
              className="h-10 w-full border border-gray-300 rounded-md cursor-pointer"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <input
              type="color"
              value={toColor}
              onChange={e =>
                setBackground(`linear-gradient(180deg, ${fromColor}, ${e.target.value})`)
              }
              className="h-10 w-full border border-gray-300 rounded-md cursor-pointer"
            />
          </div>
          <div
            className="col-span-2 h-10 rounded-md border border-gray-200"
            style={{ backgroundImage: `linear-gradient(180deg, ${fromColor}, ${toColor})` }}
            aria-hidden
          />
        </div>
      )}
    </div>
  );
};

const WidthControl: React.FC<SectionProps> = ({ data, setFormData }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">Text Width (%)</label>
    <NumberInput
      min={20}
      max={80}
      value={data.textWidth || 50}
      fallback={50}
      onChange={textWidth => {
        const imageWidth = 100 - textWidth;
        setFormData({ ...data, textWidth, imageWidth });
      }}
      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
    />
    <p className="text-xs text-gray-500 mt-1">
      Image width will automatically adjust to {100 - (data.textWidth || 50)}%
    </p>
  </div>
);

interface ImageSectionProps extends SectionProps {
  onOpenMediaUpload: (mediaType: 'image', assetType: 'image') => void;
}

const ImageSection: React.FC<ImageSectionProps> = ({ data, setFormData, onOpenMediaUpload }) => {
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const displayUrl = data.image?.cloudfrontUrl || data.image?.url;

  const handleCropApply = (cropData: CropData, _croppedAreaPixels: Area) => {
    setFormData({
      ...data,
      image: {
        ...(data.image || {}),
        enabled: data.image?.enabled ?? true,
        crop: cropData,
      },
    });
    setIsCropModalOpen(false);
  };

  return (
    <div className="border-t pt-6">
      <h3 className="text-md font-medium text-gray-900 mb-4">Image Settings</h3>

      <div className="flex items-center justify-between mb-4">
        <label className="block text-sm font-medium text-gray-700">Enable Image</label>
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={data.image?.enabled !== false}
            onChange={e =>
              setFormData({
                ...data,
                image: { ...(data.image || {}), enabled: e.target.checked },
              })
            }
            className="w-4 h-4 text-primary-600 border-gray-300 rounded-md focus:ring-primary-500"
          />
          <span className="text-sm text-gray-600">Show image</span>
        </label>
      </div>

      {data.image?.enabled !== false && (
        <div className="space-y-4 pl-4 border-l-2 border-gray-200">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Image</label>
            {displayUrl ? (
              <div className="flex items-start space-x-3">
                {/* Thumbnail with crop indicator */}
                <div className="relative">
                  <div className="w-32 h-24 rounded-md overflow-hidden border border-gray-300 bg-gray-100 relative">
                    <img
                      src={displayUrl}
                      alt={data.image?.alt || 'Preview'}
                      className={
                        hasCropApplied((data.image as any)?.crop)
                          ? undefined
                          : 'w-full h-full object-cover'
                      }
                      style={
                        hasCropApplied((data.image as any)?.crop)
                          ? getCroppedImageStyles((data.image as any)?.crop)
                          : undefined
                      }
                    />
                  </div>
                  {/* Crop indicator badge */}
                  {hasCropApplied((data.image as any)?.crop) && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center shadow-sm">
                      <Crop size={12} className="text-white" />
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex-1 space-y-2">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setIsCropModalOpen(true)}
                      className="px-3 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 rounded-md hover:bg-primary-100 flex items-center space-x-1"
                    >
                      <Crop size={14} />
                      <span>Crop</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => onOpenMediaUpload('image', 'image')}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 flex items-center space-x-1"
                    >
                      <Upload size={14} />
                      <span>Change</span>
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setFormData({
                          ...data,
                          image: {
                            ...(data.image || {}),
                            enabled: true,
                            url: '',
                            cloudfrontUrl: undefined,
                            crop: undefined,
                          },
                        })
                      }
                      className="px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 flex items-center space-x-1"
                    >
                      <Trash2 size={14} />
                      <span>Remove</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => onOpenMediaUpload('image', 'image')}
                className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-md hover:border-primary-500 text-gray-600 hover:text-primary-600 flex items-center justify-center space-x-2"
              >
                <Upload size={20} />
                <span>Upload Image</span>
              </button>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Alt Text</label>
            <input
              type="text"
              value={data.image?.alt || ''}
              onChange={e =>
                setFormData({
                  ...data,
                  image: {
                    enabled: data.image?.enabled ?? true,
                    ...(data.image || {}),
                    alt: e.target.value,
                  },
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="Image description"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Position</label>
              <select
                value={data.image?.position || 'center'}
                onChange={e =>
                  setFormData({
                    ...data,
                    image: {
                      enabled: data.image?.enabled ?? true,
                      ...(data.image || {}),
                      position: e.target.value as any,
                    },
                  })
                }
                disabled={hasCropApplied((data.image as any)?.crop)}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 ${hasCropApplied((data.image as any)?.crop) ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <option value="center">Center</option>
                <option value="top">Top</option>
                <option value="bottom">Bottom</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Size</label>
              <select
                value={data.image?.size || 'cover'}
                onChange={e =>
                  setFormData({
                    ...data,
                    image: {
                      enabled: data.image?.enabled ?? true,
                      ...(data.image || {}),
                      size: e.target.value as any,
                    },
                  })
                }
                disabled={hasCropApplied((data.image as any)?.crop)}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 ${hasCropApplied((data.image as any)?.crop) ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <option value="cover">Cover</option>
                <option value="contain">Contain</option>
                <option value="auto">Auto</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Crop Modal */}
      {displayUrl && (
        <ImageCropModal
          isOpen={isCropModalOpen}
          onClose={() => setIsCropModalOpen(false)}
          imageUrl={displayUrl}
          initialCrop={(data.image as any)?.crop}
          onApply={handleCropApply}
        />
      )}
    </div>
  );
};

const ResponsiveSection: React.FC<SectionProps> = ({ data, setFormData }) => (
  <div className="border-t pt-6">
    <h3 className="text-md font-medium text-gray-900 mb-4">Responsive Options</h3>

    <div className="space-y-3">
      <label className="flex items-center space-x-2 cursor-pointer">
        <input
          type="checkbox"
          checked={data.stackOnMobile !== false}
          onChange={e => setFormData({ ...data, stackOnMobile: e.target.checked })}
          className="w-4 h-4 text-primary-600 border-gray-300 rounded-md focus:ring-primary-500"
        />
        <span className="text-sm text-gray-700">Stack on mobile</span>
      </label>

      {data.stackOnMobile !== false && (
        <label className="flex items-center space-x-2 cursor-pointer pl-6">
          <input
            type="checkbox"
            checked={data.mobileImageFirst || false}
            onChange={e => setFormData({ ...data, mobileImageFirst: e.target.checked })}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded-md focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">Show image first on mobile</span>
        </label>
      )}
    </div>
  </div>
);

interface ColorsSectionProps extends SectionProps {
  colorFields: Array<{
    key: string;
    label: string;
    defaultValue: string;
    placeholder: string;
    hint?: string;
    allowCssColor?: boolean;
  }>;
  onUpdateColor: (key: string, value: string | null) => void;
}

const ColorsSection: React.FC<ColorsSectionProps> = ({ data, colorFields, onUpdateColor }) => (
  <ColorCustomizationPanel
    colorFields={colorFields}
    customColors={data.customColors || {}}
    onUpdateColor={onUpdateColor}
  />
);
